/* eslint-disable react/prop-types */
import dayjs from "dayjs";
import { ReactElement, useEffect, useState } from "react";

import { convertDateYYYYMMDD } from "@/constants/constant-function";
import { Autocomplete, FormControl, Grid, Stack, TextField } from "@mui/material";
import Box from "@mui/material/Box";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { SearchProps, propertyType } from "./type";

const FilterHeader: React.FC<SearchProps> = ({
  filterColumn = [],
  searchFunction,
  searchValue,
  propSearch,
  setPropSearch,
}): ReactElement => {
  const [localSearchValue, setLocalSearchValue] = useState(searchValue);
  const [localPropValues, setLocalPropValues] = useState<propertyType[]>([]);

  useEffect(() => { setLocalSearchValue(searchValue); }, [searchValue]);
  useEffect(() => { setLocalPropValues(propSearch); }, [propSearch]);

  // Debounce logic (Main Search)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearchValue !== searchValue) searchFunction(localSearchValue);
    }, 300);
    return () => clearTimeout(timer);
  }, [localSearchValue, searchFunction, searchValue]);

  // Debounce logic (Filters)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (JSON.stringify(localPropValues) !== JSON.stringify(propSearch)) {
        setPropSearch(localPropValues);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [localPropValues, setPropSearch, propSearch]);

  const handleClearAll = () => {
    setLocalPropValues([]);
    setLocalSearchValue("");
  };

  const handleDatePropSearch = (value: Date | null, element: any) => {
    let formattedDate = "";
    if (value) {
      const dateObj = new Date(value);
      if (!Number.isNaN(dateObj.getTime())) {
        const year = dateObj.getFullYear();
        if (year >= 1000 && year <= 9999) {
          const convertedDate = convertDateYYYYMMDD(dateObj);
          if (convertedDate) formattedDate = convertedDate;
        }
      }
    }

    setLocalPropValues((preVal: propertyType[]) => {
      const indexToUpdate = preVal.findIndex((e) => e.propName === element.id);
      const updatedArray = [...preVal];
      if (indexToUpdate !== -1) {
        if (formattedDate) updatedArray[indexToUpdate] = { ...updatedArray[indexToUpdate], value: formattedDate };
        else updatedArray.splice(indexToUpdate, 1);
        return updatedArray;
      }
      if (formattedDate) return [...preVal, { propName: element.id, value: formattedDate, type: "date" }];
      return preVal;
    });
  };

  const handleTextPropSearch = (value: string, element: any) => {
    setLocalPropValues((preVal: propertyType[]) => {
      const indexToUpdate = preVal.findIndex((e) => e.propName === element.id);
      let updatedArray = [...preVal];
      if (indexToUpdate !== -1) {
        updatedArray[indexToUpdate] = { ...updatedArray[indexToUpdate], value };
        return updatedArray.filter((data) => data.value !== "");
      }
      updatedArray = [...preVal, { propName: element.id, value, type: element.datatype ?? "string" }];
      return updatedArray.filter((data) => data.value !== "");
    });
  };

  const SearchComponent = (): React.ReactNode => {
    const FilterComponents = filterColumn.map((element) => {
      const property = localPropValues.find((p) => p.propName === element.id);

      if (element.datatype === "date") {
        let newDate = property?.value ? dayjs(property.value as string) : null;
        return (
          <Grid item key={`search${element.id}Grid`} xs={12} sm={6} md={3} lg={2.4}>
            <FormControl size="small" fullWidth>
              <DatePicker
                label={element.label}
                format="MMM DD, YYYY"
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: {
                      '& .MuiInputBase-root': {
                        fontSize: { xs: '0.75rem', md: '0.875rem' },
                        height: { xs: '36px', md: '40px' }
                      }
                    }
                  }
                }}
                value={newDate}
                onChange={(date: any) => handleDatePropSearch(date, element)}
              />
            </FormControl>
          </Grid>
        );
      }

      if (element.datatype === "select") {
        const options = element.selectArray?.map((sel) => ({ value: sel.id, label: sel.text })) || [];
        return (
          <Grid item key={`search${element.id}Grid`} xs={12} sm={6} md={3} lg={2.4}>
            <Autocomplete
              size="small"
              fullWidth
              options={options}
              getOptionLabel={(option) => (typeof option === "string" ? option : option.label)}
              isOptionEqualToValue={(option, value) => {
                if (!value) return false;
                return String(option.value) === String(value.value);
              }}
              value={property ? {
                value: property.value,
                label: element.selectArray?.find(sel => String(sel.id) === String(property.value))?.text || ""
              } : null}
              onChange={(_, newValue) => handleTextPropSearch(newValue?.value ? newValue.value.toString() : "", element)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={element.label}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: { xs: '0.75rem', md: '0.875rem' },
                      height: { xs: '36px', md: '40px' }
                    }
                  }}
                />
              )}
            />
          </Grid>
        );
      }

      return (
        <Grid item key={`search${element.id}Grid`} xs={12} sm={6} md={3} lg={2.4}>
          <TextField
            fullWidth
            label={element.label}
            size="small"
            value={property ? property.value : ""}
            onChange={(event) => handleTextPropSearch(event.target.value, element)}
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                height: { xs: '36px', md: '40px' }
              }
            }}
          />
        </Grid>
      );
    });

    return (
      <Box sx={{ mt: { xs: 0.5, md: 1 }, mb: { xs: 1, md: 2 } }}>
        <Stack direction="column" spacing={{ xs: 1, md: 2 }}>
          <Grid container spacing={{ xs: 1, md: 1.5 }}>
            {FilterComponents}
          </Grid>
        </Stack>
      </Box>
    );
  };

  return <>{SearchComponent()}</>;
};

export default FilterHeader;
